import { useEffect, useState, useCallback } from 'react';
import { Plus, X, Save, AlertTriangle, Shield, Trash2, ToggleLeft, ToggleRight, Upload } from 'lucide-react';
import { api } from '../../../api/client';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { CERT_FILE_ACCEPT, certFileHint } from './pfr-cert-file';

type Certificate = {
  id: string;
  locationId: string;
  locationName: string;
  pac: string;
  jid: string;
  vsdcUrl: string;
  tapUrl: string | null;
  isProduction: boolean;
  certSubject: string | null;
  certSerial: string | null;
  validFrom: string;
  expiresAt: string;
  uploadedAt: string;
  isActive: boolean;
  lastUsedAt: string | null;
  source: 'new' | 'legacy';
};

type Location = {
  id: string;
  name: string;
};

function formatDT(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function daysUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

export function CertificatesTab({ tenantId }: { tenantId: string }) {
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Certificate | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        api.get<Certificate[]>(`/admin/tenants/${tenantId}/certificates`),
        api.get<Location[]>(`/admin/locations?tenantId=${tenantId}`),
      ]);
      setCerts(c);
      setLocations(l);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [tenantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleToggle = async (cert: Certificate) => {
    await api.post(`/admin/tenants/${tenantId}/certificates/${cert.id}/toggle`, {});
    refresh();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/tenants/${tenantId}/certificates/${deleteTarget.id}`);
    refresh();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--color-text-strong)', fontWeight: 600 }}>
          <Shield size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          PFR Sertifikati ({certs.length})
        </h3>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}><Upload size={14} /> Okači sertifikat</button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead><tr style={{ background: 'var(--color-bg)' }}>
              <th style={th}>Lokacija</th>
              <th style={th}>PAC</th>
              <th style={th}>JID</th>
              <th style={th}>Env</th>
              <th style={th}>Važi od</th>
              <th style={th}>Ističe</th>
              <th style={{ ...th, textAlign: 'center' }}>Status</th>
              <th style={th}>VSDC</th>
              <th style={{ ...th, textAlign: 'right', width: 100 }}>Akcije</th>
            </tr></thead>
            <tbody>
              {certs.length === 0 && (
                <tr><td colSpan={9} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                  <Shield size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  Nema sertifikata
                </td></tr>
              )}
              {certs.map((c) => {
                const days = daysUntil(c.expiresAt);
                const expired = days <= 0;
                const expiringSoon = days > 0 && days <= 30;
                return (
                  <tr key={c.id}>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--color-text-strong)' }}>
                      {c.locationName}
                      {c.source === 'legacy' && <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 6px', borderRadius: 8, background: 'rgba(107,114,128,0.2)', color: '#9ca3af', fontWeight: 700 }}>LEGACY</span>}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{c.pac}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{c.jid}</td>
                    <td style={td}>
                      <span style={{
                        ...badge,
                        background: c.isProduction ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
                        color: c.isProduction ? '#fca5a5' : '#60a5fa',
                      }}>
                        {c.isProduction ? 'PROD' : 'TEST'}
                      </span>
                    </td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{formatDT(c.validFrom)}</td>
                    <td style={{
                      ...td, fontSize: 12, fontWeight: 600,
                      color: expired ? '#fca5a5' : expiringSoon ? '#fbbf24' : 'var(--color-text-muted)',
                    }}>
                      {formatDT(c.expiresAt)}
                      {expired && <span style={{ marginLeft: 6, fontSize: 10 }}>ISTEKAO</span>}
                      {expiringSoon && <span style={{ marginLeft: 6, fontSize: 10 }}>{days}d</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{
                        ...badge,
                        background: c.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)',
                        color: c.isActive ? '#10b981' : '#6b7280',
                      }}>
                        {c.isActive ? 'Aktivan' : 'Neaktivan'}
                      </span>
                    </td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.vsdcUrl}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => handleToggle(c)} style={iconBtn} title={c.isActive ? 'Deaktiviraj' : 'Aktiviraj'}>
                        {c.isActive ? <ToggleRight size={16} color="#10b981" /> : <ToggleLeft size={16} />}
                      </button>
                      <button onClick={() => setDeleteTarget(c)} style={{ ...iconBtn, color: '#fca5a5' }} title="Obriši">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <CertificateUploadDialog
          tenantId={tenantId}
          locations={locations}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Obriši sertifikat"
          message={`Da li želite trajno da obrišete PFR sertifikat za PAC ${deleteTarget.pac} (${deleteTarget.locationName})? Lokacija neće moći da fiskalizuje račune dok se ne učita novi sertifikat.`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

type FormProps = { tenantId: string; locations: Location[]; onClose: () => void; onSaved: () => void };

/**
 * Dva načina: ZIP od Poreske uprave (ili .p12) - server sam vadi JID, okruženje,
 * rok i proverava kod PU, kao kad firma sama otprema (ADR-062); i ručni PEM
 * unos kao rezerva (bez provere kod PU).
 */
function CertificateUploadDialog(props: FormProps) {
  useEscapeToClose(props.onClose);
  const [mode, setMode] = useState<'p12' | 'pem'>('p12');
  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={18} color="var(--color-primary)" />
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Okači PFR sertifikat</h3>
          </div>
          <button onClick={props.onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <div style={{ display: 'flex', gap: 6, padding: '14px 24px 0' }}>
          <button type="button" onClick={() => setMode('p12')} style={mode === 'p12' ? tabActive : tabIdle}>ZIP od Poreske uprave / .p12</button>
          <button type="button" onClick={() => setMode('pem')} style={mode === 'pem' ? tabActive : tabIdle}>Ručni unos (PEM)</button>
        </div>
        {mode === 'p12' ? <P12Form {...props} /> : <ManualPemForm {...props} />}
      </div>
    </div>
  );
}

function P12Form({ tenantId, locations, onClose, onSaved }: FormProps) {
  const [locationId, setLocationId] = useState(locations.length === 1 ? locations[0].id : '');
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [pac, setPac] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const hint = file ? certFileHint(file.name) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const f: Record<string, string> = {};
    if (!locationId) f.locationId = 'Izaberite lokaciju.';
    if (!file || !hint) f.file = 'Izaberite ZIP od Poreske uprave ili fajl .p12.';
    else if (!hint.ok) f.file = hint.text;
    if (!/^[A-Z0-9]{6}$/.test(pac)) f.pac = 'PAC ima 6 znakova (slova i cifre).';
    setFields(f);
    setError(null);
    if (Object.keys(f).length || !file) return;
    setSaving(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let bin = '';
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      await api.post(`/admin/tenants/${tenantId}/certificates/p12`, { locationId, pac, password, fileBase64: btoa(bin), fileName: file.name });
      onSaved();
    } catch (e: any) {
      if (e?.data?.fields) setFields(e.data.fields);
      setError(e?.data?.message || e?.message || 'Sertifikat nije sačuvan.');
    } finally {
      setSaving(false);
    }
  };

  const fieldErr = (k: string) => fields[k] ? <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 4 }}>{fields[k]}</div> : null;

  return (
    <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
        Poreska uprava šalje <b>ZIP</b> čije je ime JID (npr. <code>FB9PVGZT.zip</code>) sa sedam fajlova. Okačite <b>ceo ZIP</b>,
        ili iz njega <code>1_…nochain.p12</code>. JID, okruženje, rok i adrese se čitaju iz sertifikata, a pre upisa se proverava
        kod Poreske uprave da sertifikat radi i da PAC odgovara.
      </div>
      <Field label="Lokacija *">
        <select value={locationId} onChange={(e) => setLocationId(e.target.value)} style={input}>
          <option value="">- Izaberi lokaciju -</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {fieldErr('locationId')}
      </Field>
      <Field label="ZIP od Poreske uprave ili .p12 *">
        <input type="file" accept={CERT_FILE_ACCEPT} onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setFile(f);
          setFields((x) => ({ ...x, file: f && !certFileHint(f.name).ok ? certFileHint(f.name).text : '' }));
        }} style={{ fontSize: 12 }} />
        {file && hint?.ok && <div style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>{file.name} - {hint.text}</div>}
        {fieldErr('file')}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Lozinka sertifikata">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" style={input} />
          {fieldErr('password')}
        </Field>
        <Field label="PAC *">
          <input type="text" value={pac} onChange={(e) => setPac(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))} placeholder="JJRD9X" autoComplete="off" style={{ ...input, fontFamily: 'monospace' }} />
          {fieldErr('pac')}
        </Field>
      </div>
      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
        <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
          <Save size={14} /> {saving ? 'Proveravam kod PU…' : 'Okači i proveri'}
        </button>
      </div>
    </form>
  );
}

function ManualPemForm({ tenantId, locations, onClose, onSaved }: FormProps) {
  const [locationId, setLocationId] = useState('');
  const [pac, setPac] = useState('');
  const [jid, setJid] = useState('');
  const [vsdcUrl, setVsdcUrl] = useState('vsdc.sandbox.suf.purs.gov.rs');
  const [tapUrl, setTapUrl] = useState('tap.sandbox.suf.purs.gov.rs');
  const [isProduction, setIsProduction] = useState(false);
  const [certPem, setCertPem] = useState('');
  const [keyPem, setKeyPem] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [certSubject, setCertSubject] = useState('');
  const [certSerial, setCertSerial] = useState('');
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Šta je server pročitao iz sertifikata - rok i ostalo se ne kucaju (server ih i pri upisu uzima iz sertifikata).
  const [fromCert, setFromCert] = useState<{ text: string; warn: string | null } | null>(null);

  useEffect(() => {
    if (!certPem.includes('BEGIN CERTIFICATE')) { setFromCert(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.post<{
          jid: string; isProduction: boolean; vsdcUrl: string; tapUrl: string; subject: string; serial: string;
          validFrom: string; expiresAt: string; message: string | null;
        }>(`/admin/tenants/${tenantId}/certificates/inspect`, { certPem });
        if (cancelled) return;
        const day = (iso: string) => new Date(iso).toLocaleDateString('sv-SE'); // YYYY-MM-DD, lokalno
        setValidFrom(day(r.validFrom));
        setExpiresAt(day(r.expiresAt));
        if (r.jid) setJid(r.jid);
        setIsProduction(r.isProduction);
        setVsdcUrl(r.vsdcUrl.replace(/^https:\/\//, ''));
        setTapUrl(r.tapUrl.replace(/^https:\/\//, ''));
        setCertSubject(r.subject);
        setCertSerial(r.serial);
        setFromCert({
          text: `Pročitano iz sertifikata: važi od ${formatDT(r.validFrom)} do ${formatDT(r.expiresAt)}${r.jid ? `, JID ${r.jid}` : ''}, ${r.isProduction ? 'produkcija' : 'test (sandbox)'}.`,
          warn: r.message,
        });
      } catch (e: any) {
        if (!cancelled) setFromCert({ text: '', warn: e?.data?.message || 'Sertifikat ne može da se pročita - rok upišite ručno.' });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [certPem, tenantId]);
  const datesFromCert = !!fromCert?.text;

  const handleFileRead = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      setter(text);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locationId || !pac || !jid || !vsdcUrl || !certPem || !keyPem || !expiresAt) {
      setError('Sva obavezna polja moraju biti popunjena');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/certificates`, {
        locationId,
        pac: pac.trim(),
        jid: jid.trim(),
        vsdcUrl: vsdcUrl.trim(),
        tapUrl: tapUrl.trim() || undefined,
        isProduction,
        certPem,
        keyPem,
        passphrase: passphrase || undefined,
        certSubject: certSubject || undefined,
        certSerial: certSerial || undefined,
        validFrom: new Date(validFrom).toISOString(),
        expiresAt: new Date(expiresAt).toISOString(),
      });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri uploadu');
    } finally {
      setSaving(false);
    }
  };

  return (
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '70vh', overflow: 'auto' }}>

          <Section title="Lokacija i identifikacija">
            <Field label="Lokacija *">
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} required style={input}>
                <option value="">- Izaberi lokaciju -</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="PAC *"><input type="text" value={pac} onChange={(e) => setPac(e.target.value)} placeholder="JJRD9X" required style={input} /></Field>
              <Field label="JID *"><input type="text" value={jid} onChange={(e) => setJid(e.target.value)} placeholder="49JR4QUC" required style={input} /></Field>
            </div>
          </Section>

          <Section title="SUF konekcija">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="VSDC URL *"><input type="text" value={vsdcUrl} onChange={(e) => setVsdcUrl(e.target.value)} required style={input} /></Field>
              <Field label="TAP URL"><input type="text" value={tapUrl} onChange={(e) => setTapUrl(e.target.value)} style={input} /></Field>
            </div>
            <Field label="Okruženje *">
              <select value={isProduction ? 'prod' : 'test'} onChange={(e) => {
                const prod = e.target.value === 'prod';
                setIsProduction(prod);
                if (prod) { setVsdcUrl('vsdc.suf.purs.gov.rs'); setTapUrl('tap.suf.purs.gov.rs'); }
                else { setVsdcUrl('vsdc.sandbox.suf.purs.gov.rs'); setTapUrl('tap.sandbox.suf.purs.gov.rs'); }
              }} style={input}>
                <option value="test">Test (sandbox)</option>
                <option value="prod">Produkcija</option>
              </select>
            </Field>
          </Section>

          <Section title="Sertifikat fajlovi">
            <div style={{ padding: 10, background: 'rgba(245,158,11,0.1)', borderRadius: 6, fontSize: 12, color: '#fbbf24' }}>
              Sertifikat i privatni ključ će biti enkriptovani AES-256-GCM pre upisa u bazu. Jednom okačeni, ne mogu se preuzeti nazad kroz UI.
            </div>
            <Field label="Certificate PEM * (fajl ili paste)">
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="file" accept=".pem,.crt,.cer" onChange={handleFileRead(setCertPem)} style={{ fontSize: 12 }} />
              </div>
              <textarea
                value={certPem}
                onChange={(e) => setCertPem(e.target.value)}
                rows={4}
                placeholder="-----BEGIN CERTIFICATE-----&#10;..."
                style={{ ...input, fontFamily: 'monospace', fontSize: 11, marginTop: 6, resize: 'vertical' }}
              />
            </Field>
            <Field label="Private Key PEM * (fajl ili paste)">
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="file" accept=".pem,.key" onChange={handleFileRead(setKeyPem)} style={{ fontSize: 12 }} />
              </div>
              <textarea
                value={keyPem}
                onChange={(e) => setKeyPem(e.target.value)}
                rows={4}
                placeholder="-----BEGIN PRIVATE KEY-----&#10;..."
                style={{ ...input, fontFamily: 'monospace', fontSize: 11, marginTop: 6, resize: 'vertical' }}
              />
            </Field>
            <Field label="Passphrase (opciono)">
              <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} style={input} />
            </Field>
          </Section>

          <Section title="Validnost">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Važi od *"><input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} readOnly={datesFromCert} required style={{ ...input, opacity: datesFromCert ? 0.75 : 1 }} /></Field>
              <Field label="Ističe *"><input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} readOnly={datesFromCert} required style={{ ...input, opacity: datesFromCert ? 0.75 : 1 }} /></Field>
            </div>
            {!fromCert && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Rok se popunjava sam kad učitate sertifikat (Certificate PEM).</div>}
            {fromCert?.text && <div style={{ fontSize: 12, color: '#10b981' }}>{fromCert.text}</div>}
            {fromCert?.warn && <div style={{ fontSize: 12, color: '#fbbf24' }}>{fromCert.warn}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Subject (informativno)"><input type="text" value={certSubject} onChange={(e) => setCertSubject(e.target.value)} style={input} /></Field>
              <Field label="Serial (informativno)"><input type="text" value={certSerial} onChange={(e) => setCertSerial(e.target.value)} style={input} /></Field>
            </div>
          </Section>

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
      <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div>{label && <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>}{children}</div>;
}

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const badge: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600 };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 640, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)', maxHeight: '92vh', overflow: 'auto' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, background: 'var(--color-bg-surface)', zIndex: 1 };
const tabIdle: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-muted)', fontSize: 12, fontWeight: 500, cursor: 'pointer' };
const tabActive: React.CSSProperties = { ...tabIdle, background: 'var(--color-bg)', color: 'var(--color-text-strong)', borderColor: 'var(--color-primary)', fontWeight: 600 };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg-surface)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
